/** @format */

import { Button, Divider, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { buttonColors, colors } from "../../utility/types/constant";

// Modern Slideshow Container
export const SlideShowContainer = styled(Box)(({ theme }) => ({
	position: "relative",
	width: "100%",
	height: "500px",
	borderRadius: "24px",
	overflow: "hidden",
	boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
	[theme.breakpoints.down("md")]: {
		height: "450px",
		borderRadius: "16px",
	},
	[theme.breakpoints.down("sm")]: {
		height: "550px",
	},
}));

interface SlideWrapperProps {
	isActive: boolean;
	slideColor: string;
}

export const SlideWrapper = styled(Box, {
	shouldForwardProp: (prop) => prop !== "isActive" && prop !== "slideColor",
})<SlideWrapperProps>(({ theme, isActive, slideColor }) => ({
	position: "absolute",
	top: 0,
	left: 0,
	width: "100%",
	height: "100%",
	backgroundColor: slideColor,
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	padding: theme.spacing(6),
	opacity: isActive ? 1 : 0,
	transform: isActive ? "translateX(0)" : "translateX(100%)",
	transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
	zIndex: isActive ? 1 : 0,
	[theme.breakpoints.down("sm")]: {
		padding: theme.spacing(4),
	},
}));

export const OverlayGradient = styled(Box)(({ theme }) => ({
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	background:
		"radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
	pointerEvents: "none",
	zIndex: 2,
}));

export const SlideContent = styled(Box)(({ theme }) => ({
	textAlign: "center",
	maxWidth: "800px",
	color: "#ffffff",
	position: "relative",
	zIndex: 3,
}));

export const SlideNumber = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "40px",
	right: "60px",
	fontSize: "clamp(80px, 12vw, 150px)",
	fontWeight: 900,
	color: "rgba(255,255,255,0.08)",
	fontFamily: "'Montserrat', sans-serif",
	lineHeight: 1,
	zIndex: 1,
	[theme.breakpoints.down("sm")]: {
		top: "20px",
		right: "20px",
		fontSize: "60px",
	},
}));

export const SlideTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(2rem, 4vw, 3.5rem)",
	fontWeight: 800,
	color: "#ffffff",
	marginBottom: theme.spacing(2),
	letterSpacing: "-0.02em",
	lineHeight: 1.2,
	textShadow: "0 2px 20px rgba(0,0,0,0.2)",
}));

export const SlideDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
	fontWeight: 400,
	color: "rgba(255,255,255,0.95)",
	marginBottom: theme.spacing(4),
	lineHeight: 1.7,
	maxWidth: "700px",
	margin: "0 auto",
}));

export const CTAButton = styled(Button)(({ theme }) => ({
	backgroundColor: "#ffffff",
	color: colors.primary,
	padding: "16px 48px",
	fontSize: "1.125rem",
	fontWeight: 600,
	textTransform: "none",
	borderRadius: "50px",
	fontFamily: "'Montserrat', sans-serif",
	boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
	transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
	"&:hover": {
		backgroundColor: "#f8f9fa",
		transform: "translateY(-3px)",
		boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
	},
	[theme.breakpoints.down("sm")]: {
		padding: "14px 36px",
		fontSize: "1rem",
	},
}));

// Dots Navigation
export const DotsContainer = styled(Box)(({ theme }) => ({
	position: "absolute",
	bottom: "30px",
	left: "50%",
	transform: "translateX(-50%)",
	display: "flex",
	gap: "12px",
	zIndex: 10,
	[theme.breakpoints.down("sm")]: {
		bottom: "20px",
	},
}));

interface DotProps {
	isActive: boolean;
}

export const Dot = styled(Box, {
	shouldForwardProp: (prop) => prop !== "isActive",
})<DotProps>(({ theme, isActive }) => ({
	width: isActive ? "40px" : "12px",
	height: "12px",
	borderRadius: "6px",
	backgroundColor: isActive ? "#ffffff" : "rgba(255,255,255,0.4)",
	cursor: "pointer",
	transition: "all 0.3s ease",
	"&:hover": {
		backgroundColor: "rgba(255,255,255,0.8)",
	},
}));

// Legacy styles for backwards compatibility
export const SectionContainer = styled("div")(({ theme }) => ({
	backgroundColor: colors.primary,
	textAlign: "center",
	marginTop: 30,
	marginBottom: 150,
	width: "100%",
	height: "280px",
	alignItems: "center",
	justifyContent: "center",

	[theme.breakpoints.down("sm")]: {
		marginTop: "400px",
		marginBottom: "30px",
	},

	[theme.breakpoints.down("md")]: {
		marginTop: 90,
		marginBottom: 50,
	},
}));

export const SectionContentHeader = styled(Typography)(({ theme }) => ({
	padding: "20px",
	justifyContent: "center",
	alignContent: "center",
	alignItems: "center",
	maxWidth: "100%",
	display: "flex",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "32.58px",
	fontWeight: "bold",
	color: "#fff",
}));

export const SectionContentSubHeader = styled(Typography)(({ theme }) => ({
	padding: "10px",
	justifyContent: "center",
	maxWidth: "100%",
	display: "flex",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: 18.5,
	fontWeight: "bold",
	fontStyle: "italic",
	color: "#ffffff",
}));

export const JoinButton = styled(Button)(({ theme }) => ({
	padding: "9px",
	border: "none",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "25.58px",
	fontWeight: 500,
	backgroundColor: buttonColors.primary,
	justifyContent: "center",
	alignContent: "center",

	cursor: "pointer",
	color: "#FFFFFF",
	minWidth: "280px",
	"&:hover": {
		backgroundColor: buttonColors.primary,
	},
	textTransform: "none",
}));

export const StyledDivider = styled(Divider)({
	width: "100%",
	marginBottom: "20px",
	marginTop: "20px",
});
